
import { Component, Vue } from "vue-property-decorator";
import * as Models from "@gigalot/data-models";
import { v4 as uuidv4 } from "uuid";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";

type Nullable<T> = T | null | undefined;

interface MovementRow {
  id: string;
  fromKraalGuid: Nullable<string>;
  toKraalGuid: Nullable<string>;
  morbidity: boolean;
  quantity: Nullable<number>;
}

interface KraalOption {
  guid: string;
  label: string;
}

@Component({
  components: {
    BreadcrumbTrail,
  },
})
export default class AnimalMovements extends Vue {
  loadingKraals = false;
  submitting = false;
  isFormValid = true;
  errorMessage = "";
  successMessage = "";
  kraals: Models.Kraal[] = [];
  animalCounts: Record<string, number> = {};
  movementRows: MovementRow[] = [this.createMovementRow()];

  get kraalOptions(): KraalOption[] {
    return this.kraals
      .map((kraal) => ({
        guid: kraal.guid,
        label: this.buildKraalLabel(kraal),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  kraalSelectionRules = [(value: Nullable<string>) => !!value || "Select a kraal"];

  private createMovementRow(): MovementRow {
    return {
      id: uuidv4(),
      fromKraalGuid: null,
      toKraalGuid: null,
      morbidity: false,
      quantity: null,
    };
  }

  async mounted() {
    await this.fetchKraalsAndCounts();
  }

  private buildKraalLabel(kraal: Models.Kraal): string {
    if (kraal.description) {
      return `${kraal.kraalId} - ${kraal.description}`;
    }
    return kraal.kraalId;
  }

  private filterKraal(item: KraalOption, queryText: string, itemText: string): boolean {
    const text = (item.label || "").toLowerCase();
    const search = (queryText || "").toLowerCase();
    return text.includes(search);
  }

  private toKraalOptions(row: MovementRow): KraalOption[] {
    if (!row.fromKraalGuid) {
      return this.kraalOptions;
    }
    return this.kraalOptions.filter((option) => option.guid !== row.fromKraalGuid);
  }

  private getAnimalCount(guid: Nullable<string>): Nullable<number> {
    if (!guid) {
      return null;
    }
    const count = this.animalCounts[guid];
    return typeof count === "number" ? count : null;
  }

  formatAnimalCount(guid: Nullable<string>): string {
    const count = this.getAnimalCount(guid);
    if (count === null || count === undefined) {
      return "Unknown";
    }
    return count.toString();
  }

  addMovementRow(): void {
    this.movementRows.push(this.createMovementRow());
  }

  removeMovementRow(index: number): void {
    if (this.movementRows.length === 1) {
      return;
    }
    this.movementRows.splice(index, 1);
  }

  onFromKraalChange(row: MovementRow): void {
    if (row.toKraalGuid === row.fromKraalGuid) {
      row.toKraalGuid = null;
    }
    const available = this.getAnimalCount(row.fromKraalGuid);
    if (available !== null && available !== undefined && row.quantity && row.quantity > available) {
      row.quantity = available;
    }
  }

  onMorbidityChange(row: MovementRow): void {
    if (row.morbidity) {
      row.toKraalGuid = null;
    }
  }

  toKraalRules(row: MovementRow) {
    return [
      (value: Nullable<string>) => {
        if (row.morbidity) {
          return true;
        }
        return !!value || "Select a kraal";
      },
      (value: Nullable<string>) => {
        if (row.morbidity || !value || !row.fromKraalGuid) {
          return true;
        }
        return value !== row.fromKraalGuid || "To kraal must be different";
      },
    ];
  }

  quantityRules(row: MovementRow) {
    return [
      (value: Nullable<number>) => (value !== null && value !== undefined) || "Quantity is required",
      (value: Nullable<number>) => {
        if (value === null || value === undefined) {
          return true;
        }
        return Number(value) > 0 || "Quantity must be greater than 0";
      },
      (value: Nullable<number>) => {
        const available = this.getAnimalCount(row.fromKraalGuid);
        if (available === null || available === undefined || value === null || value === undefined) {
          return true;
        }
        return Number(value) <= available || `Only ${available} animals available`;
      },
    ];
  }

  async fetchKraalsAndCounts(): Promise<void> {
    this.loadingKraals = true;
    this.errorMessage = "";
    try {
      const gql = `query AnimalMovementData($guid: String!) {
        Kraals(guid: $guid) {
          guid
          kraalId
          description
        }
        CribScores(guid: $guid) {
          cribScores {
            kraalGuid
            animalCount
          }
        }
      }`;
      const variables = { guid: this.$store.state.user.location.guid };
      const response = await this.$store.dispatch("graphQl", { gql, variables }, { root: true });

      if (response?.errors?.length) {
        throw new Error(response.errors.map((err: any) => err.message).join(", "));
      }

      this.kraals = (response?.data?.Kraals ?? []) as Models.Kraal[];
      const cribScores = response?.data?.CribScores?.cribScores ?? [];
      this.animalCounts = {};
      cribScores.forEach((score: any) => {
        if (score?.kraalGuid) {
          this.animalCounts[score.kraalGuid] = score.animalCount ?? 0;
        }
      });
    } catch (err) {
      console.error(err);
      this.errorMessage = err instanceof Error ? err.message : String(err);
    } finally {
      this.loadingKraals = false;
    }
  }

  async submitMovements(): Promise<void> {
    const form = this.$refs.movementForm as (Vue & { validate: () => boolean; resetValidation: () => void }) | undefined;
    this.errorMessage = "";
    this.successMessage = "";

    if (form && !form.validate()) {
      this.errorMessage = "Please resolve validation errors before submitting.";
      return;
    }

    const movements = this.movementRows.map((row) => ({
      fromKraalGuid: row.fromKraalGuid as string,
      toKraalGuid: row.morbidity ? "" : row.toKraalGuid,
      noAnimalsAtMove: Number(row.quantity),
    }));

    this.submitting = true;
    try {
      const gql = `mutation MoveKraals($guid: String!, $movements: [MoveKraalInput!]!, $user: String) {
        moveKraals(guid: $guid, movements: $movements, user: $user)
      }`;
      const variables = {
        guid: this.$store.state.user.location.guid,
        movements,
        user: JSON.stringify({ userId: this.$store.state.user.user.uid, userName: this.$store.state.user.user.displayName || "", userEmail: this.$store.state.user.user.email || "" }),
      };
      const response = await this.$store.dispatch("graphQl", { gql, variables }, { root: true });

      if (response?.errors?.length) {
        throw new Error(response.errors.map((err: any) => err.message).join(", "));
      }

      if (response?.data?.moveKraals !== "OK") {
        throw new Error(response?.data?.moveKraals || "Animal movement was not accepted.");
      }

      this.successMessage = "Animal movements submitted successfully.";
      this.resetForm(true);
      await this.fetchKraalsAndCounts();
    } catch (err) {
      console.error(err);
      this.errorMessage = err instanceof Error ? err.message : String(err);
    } finally {
      this.submitting = false;
    }
  }

  resetForm(preserveSuccess = false): void {
    this.movementRows = [this.createMovementRow()];
    const form = this.$refs.movementForm as (Vue & { resetValidation: () => void }) | undefined;
    form?.resetValidation();
    if (!preserveSuccess) {
      this.successMessage = "";
    }
    this.errorMessage = "";
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Animal Movements", disabled: true });
  }
}
